import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';
import { 
  Users, Clock, CheckCircle, AlertTriangle, TrendingUp, 
  Brain, Sparkles, Activity, BarChart3, Zap, Target,
  ArrowUp, ArrowDown, Minus, RefreshCw, Calendar, Shield
} from 'lucide-react';

export default function DnaGoDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    retiradas_hoje: 0,
    fila_atual: 0,
    tempo_medio_minutos: 0,
    ocorrencias_abertas: 0,
    taxa_verificacao_facial: 0
  });
  const [retiradas, setRetiradas] = useState([]);
  const [insights, setInsights] = useState([]);
  const [turmasStats, setTurmasStats] = useState([]);
  const [horasPico, setHorasPico] = useState([]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [dashRes, retiradasRes] = await Promise.all([
        api.get('/dnago/dashboard'),
        api.get('/dnago/retiradas?limit=100')
      ]);
      
      setStats(dashRes.data);
      setRetiradas(retiradasRes.data || []);
      
      // Processar estatísticas
      processarEstatisticas(retiradasRes.data || []);
      gerarInsightsIA(dashRes.data, retiradasRes.data || []);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const processarEstatisticas = (retiradas) => {
    // Estatísticas por turma
    const turmasMap = {};
    retiradas.forEach(r => {
      if (!turmasMap[r.turma_nome]) {
        turmasMap[r.turma_nome] = { total: 0, tempos: [] };
      }
      turmasMap[r.turma_nome].total += 1;
    });
    
    const turmasArray = Object.entries(turmasMap)
      .map(([nome, data]) => ({ nome, ...data }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
    
    setTurmasStats(turmasArray);

    // Horas de pico
    const horasMap = {};
    retiradas.forEach(r => {
      const hora = new Date(r.hora_retirada).getHours();
      horasMap[hora] = (horasMap[hora] || 0) + 1;
    });
    
    const horasArray = Object.entries(horasMap)
      .map(([hora, count]) => ({ hora: parseInt(hora), count }))
      .sort((a, b) => a.hora - b.hora);
    
    setHorasPico(horasArray);
  };

  const gerarInsightsIA = (stats, retiradas) => {
    const newInsights = [];
    
    // Análise de performance
    if (stats.tempo_medio_minutos < 2) {
      newInsights.push({
        tipo: 'sucesso',
        icone: TrendingUp,
        titulo: 'Excelente Performance',
        texto: `Tempo médio de ${stats.tempo_medio_minutos} minutos está abaixo da meta.`,
        cor: 'text-green-600',
        bg: 'bg-green-50'
      });
    } else if (stats.tempo_medio_minutos > 5) {
      newInsights.push({
        tipo: 'alerta',
        icone: AlertTriangle,
        titulo: 'Tempo Acima do Ideal',
        texto: `Considere abrir mais pontos de saída.`,
        cor: 'text-amber-600',
        bg: 'bg-amber-50'
      });
    }

    // Previsão de fila
    const horaAtual = new Date().getHours();
    if (horaAtual >= 11 && horaAtual <= 12) {
      newInsights.push({
        tipo: 'previsao',
        icone: Zap,
        titulo: 'Horário de Pico',
        texto: 'Prepare-se para aumento no fluxo de saída.',
        cor: 'text-purple-600',
        bg: 'bg-purple-50'
      });
    }

    // Taxa de verificação facial
    if (stats.taxa_verificacao_facial > 70) {
      newInsights.push({
        tipo: 'seguranca',
        icone: Shield,
        titulo: 'Alta Segurança',
        texto: `${stats.taxa_verificacao_facial}% das retiradas com verificação facial.`,
        cor: 'text-blue-600',
        bg: 'bg-blue-50'
      });
    }

    // Sugestões de otimização
    if (stats.fila_atual > 10) {
      newInsights.push({
        tipo: 'sugestao',
        icone: Target,
        titulo: 'Otimização Sugerida',
        texto: 'Priorize turmas com mais alunos aguardando.',
        cor: 'text-indigo-600',
        bg: 'bg-indigo-50'
      });
    }

    setInsights(newInsights);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <Card className={`bg-gradient-to-br ${color} border-0 text-white overflow-hidden`}>
      <CardContent className="p-6">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-white/70 text-sm">{title}</p>
            <p className="text-4xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-white/70 text-xs mt-1">{subtitle}</p>}
          </div>
          <div className="p-3 bg-white/20 rounded-xl">
            <Icon className="w-8 h-8" />
          </div>
        </div>
        {trend !== undefined && (
          <div className="mt-4 flex items-center gap-1 text-sm">
            {trend > 0 ? <ArrowUp className="w-4 h-4" /> : 
             trend < 0 ? <ArrowDown className="w-4 h-4" /> : 
             <Minus className="w-4 h-4" />}
            <span>{Math.abs(trend)}% vs ontem</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-purple-600" />
            Dashboard DNA GO
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Visão geral e inteligência operacional
          </p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Retiradas Hoje"
          value={stats.retiradas_hoje}
          icon={CheckCircle}
          color="from-green-500 to-emerald-600"
          trend={12}
        />
        <StatCard
          title="Fila Atual"
          value={stats.fila_atual}
          subtitle="alunos aguardando"
          icon={Users}
          color="from-purple-500 to-violet-600"
        />
        <StatCard
          title="Tempo Médio"
          value={`${stats.tempo_medio_minutos}min`}
          subtitle="por retirada"
          icon={Clock}
          color="from-blue-500 to-cyan-600"
          trend={-5}
        />
        <StatCard
          title="Alertas"
          value={stats.ocorrencias_abertas}
          subtitle="pendentes"
          icon={AlertTriangle}
          color="from-amber-500 to-orange-600"
        />
      </div>

      {/* IA Insights */}
      {insights.length > 0 && (
        <Card className="border-purple-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-600" />
              Insights da IA Operacional
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {insights.map((insight, idx) => {
                const Icon = insight.icone;
                return (
                  <div key={idx} className={`p-4 rounded-xl ${insight.bg} border`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg bg-white ${insight.cor}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className={`font-semibold ${insight.cor}`}>{insight.titulo}</p>
                        <p className="text-sm text-gray-600 mt-1">{insight.texto}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grid de análises */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Turmas mais ativas */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Retiradas por Turma</CardTitle>
            <CardDescription>Top 5 turmas com mais retiradas hoje</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {turmasStats.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Sem dados ainda</p>
              ) : (
                turmasStats.map((turma, idx) => (
                  <div key={turma.nome} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
                      idx === 0 ? 'bg-purple-600' : 
                      idx === 1 ? 'bg-purple-500' : 
                      'bg-purple-400'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-sm">{turma.nome}</span>
                        <span className="text-sm text-gray-500">{turma.total}</span>
                      </div>
                      <Progress 
                        value={(turma.total / (turmasStats[0]?.total || 1)) * 100} 
                        className="h-2"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Horários de Pico */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Horários de Pico</CardTitle>
            <CardDescription>Distribuição de retiradas por hora</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {horasPico.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Sem dados ainda</p>
              ) : (
                horasPico.map(({ hora, count }) => {
                  const maxCount = Math.max(...horasPico.map(h => h.count));
                  const percentage = (count / maxCount) * 100;
                  return (
                    <div key={hora} className="flex items-center gap-3">
                      <span className="w-12 text-sm text-gray-500 text-right">
                        {hora.toString().padStart(2, '0')}:00
                      </span>
                      <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${percentage}%` }}
                        >
                          {percentage > 20 && (
                            <span className="text-white text-xs font-medium">{count}</span>
                          )}
                        </div>
                      </div>
                      {percentage <= 20 && (
                        <span className="text-xs text-gray-500 w-8">{count}</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Taxa de Segurança */}
      <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-purple-600 rounded-xl">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-purple-900">Índice de Segurança</h3>
                <p className="text-purple-600">Taxa de verificação facial nas retiradas</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-purple-700">{stats.taxa_verificacao_facial}%</p>
                <p className="text-sm text-purple-500">Verificação Facial</p>
              </div>
              <div className="h-16 w-px bg-purple-200" />
              <div className="text-center">
                <p className="text-4xl font-bold text-green-600">{stats.retiradas_hoje}</p>
                <p className="text-sm text-gray-500">Total Hoje</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Últimas Retiradas */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-600" />
            Atividade Recente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {retiradas.slice(0, 5).map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-semibold">
                      {r.aluno_nome?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{r.aluno_nome}</p>
                    <p className="text-xs text-gray-500">{r.turma_nome}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm">{r.autorizado_nome}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(r.hora_retirada).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                {r.face_verified && (
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Face
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
